import { CircularProgress, Typography } from '@material-ui/core';
import { Close, Delete } from '@material-ui/icons';
import {
  Column,
  Columns,
  Inline,
  OverflowText,
  Stack,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { LoadsImportResult } from '../../data/LoadsAPI';
import { ErrorIcon } from 'shared/icons/ErrorIcon';
import { CsvIcon } from 'shared/icons/CsvIcon';
import { PdfFileIcon } from 'shared/icons/PdfFileIcon';
import { ButtonBase } from '@material-ui/core';
import { LinkAnchor } from 'shared/routing/Links';
import { UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from 'shared/icons/CheckIcon';
import { ReplayIcon } from 'shared/icons/ReplayIcon';
import styled from 'styled-components';
import { useState } from 'react';
import { CancelIcon } from 'shared/icons/CancelIcon';

const ListItem = styled.div<{ isHovered: boolean }>`
  cursor: ${({ isHovered }) => (isHovered ? 'pointer' : 'default')};
`;

export const IconWrapper = styled.div`
  margin-top: 2px;
`;

const ErrorWrapper = styled.div`
  padding-right: 16px;
  word-break: break-all;
  white-space: pre-wrap;
`;

export function FileListItem({
  data,
  file,
}: {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
  data: UseQueryResult<LoadsImportResult, Error>;
  file?: File;
}) {
  const [isHovered, setHovered] = useState(false);
  const queryClient = useQueryClient();
  const isDeleted = data.data && data.data.some((item) => !item.guid);
  const isCancelled = data.fetchStatus === 'idle' && data.status === 'pending';
  if (isDeleted) {
    return null;
  }

  return (
    <ListItem
      role="button"
      aria-label="Cancel file import"
      isHovered={data.isLoading && isHovered}
      onMouseEnter={() => {
        if (data.isLoading) {
          setHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (data.isLoading) {
          setHovered(false);
        }
      }}
      onClick={() => {
        if (data.isLoading && isHovered) {
          void queryClient.cancelQueries({
            queryKey: ['loads-import', file?.name],
          });
        }
      }}
    >
      <Stack space="xxsmall">
        <Columns space="xsmall">
          <Column width="fluid">
            <Columns space="xsmall" align="top">
              <Column width="content">
                <IconWrapper>
                  {handleFileType(data.isError, isCancelled, file?.type)}
                </IconWrapper>
              </Column>
              <Column width="fluid">
                <Stack space="xsmall">
                  <Columns align="center">
                    <Column width="fluid">
                      <OverflowText disableUnderline={true} variant="body1">
                        {file?.name}
                      </OverflowText>
                    </Column>
                    {data.isError && (
                      <Column width="content">
                        <ButtonBase
                          onClick={() =>
                            void queryClient.resetQueries({
                              queryKey: ['loads-import', file?.name],
                            })
                          }
                          title="Retry"
                        >
                          <ReplayIcon fontSize="small" color="action" />
                        </ButtonBase>
                      </Column>
                    )}
                  </Columns>
                  {data.data &&
                    data.data.length > 0 &&
                    data.data.map((load, index) => (
                      <LinkAnchor
                        key={index}
                        to={`/loads/${load.guid}`}
                        target="_blank"
                      >
                        {load.number || `ORD-${index + 1}`}
                      </LinkAnchor>
                    ))}
                </Stack>
              </Column>
            </Columns>
          </Column>
          <Column width="content">
            <Box paddingRight="small">
              <Inline horizontalAlign="right">
                {isHovered && data.isLoading && (
                  <Close fontSize="small" color="error" />
                )}
                {!isHovered && data.isLoading && (
                  <CircularProgress size={14} title="Loading" />
                )}
                {data.isSuccess && <CheckIcon fontSize="small" />}
                {data.isError && (
                  <ButtonBase
                    title="Delete"
                    onClick={() => {
                      queryClient.setQueryData(
                        ['loads-import', file?.name],
                        [{ guid: null }],
                      );
                    }}
                  >
                    <Delete fontSize="small" color="action" />
                  </ButtonBase>
                )}
              </Inline>
            </Box>
          </Column>
        </Columns>
        {data.isError && (
          <ErrorWrapper>
            <Typography color="error">{data.error.message}</Typography>
          </ErrorWrapper>
        )}
      </Stack>
    </ListItem>
  );
}

function handleFileType(isError: boolean, cancelled: boolean, type?: string) {
  if (cancelled) {
    return <CancelIcon />;
  }
  if (isError) {
    return <ErrorIcon fontSize="small" color="error" />;
  }
  switch (type) {
    case 'application/pdf':
      return <PdfFileIcon />;
    case 'text/csv':
      return <CsvIcon />;
    default:
      return null;
  }
}
